<template>
  <div>
    <v-dialog
      v-model="dialog"
      fullscreen
      hide-overlay
      persistent
      transition="dialog-bottom-transition"
    >
      <v-card class="trim-editor">
        <!-- Enhanced Header Toolbar -->
        <v-toolbar elevation="0" class="toolbar-enhanced">
          <v-btn icon variant="text" @click="close" class="close-btn">
            <v-icon size="24">mdi-close</v-icon>
          </v-btn>

          <v-toolbar-title class="toolbar-title">
            <v-img
              max-height="60"
              max-width="120"
              :src="require('@/assets/logo.png')"
            ></v-img>
          </v-toolbar-title>

          <v-spacer />

          <!-- Duration Badge -->
          <v-chip
            color="success"
            variant="outlined"
            class="mr-4 duration-chip"
            prepend-icon="mdi-clock-outline"
          >
            {{ formatDuration(trimOut - trimIn) }} selected
          </v-chip>

          <v-btn
            variant="outlined"
            color="warning"
            @click="revert"
            class="mr-3"
            :disabled="trimIn === 0 && trimOut === duration"
          >
            <v-icon left>mdi-restore</v-icon>
            Reset
          </v-btn>

          <v-btn
            color="primary"
            variant="elevated"
            :loading="saving || isProcessing"
            :disabled="saving || isProcessing"
            @click="saveTrimmedVideo"
            class="save-btn"
          >
            <v-icon left>{{ isProcessing ? "mdi-cog" : "mdi-check" }}</v-icon>
            {{ isProcessing ? "Processing..." : "Apply Trim" }}
          </v-btn>
        </v-toolbar>

        <!-- Video Player Container -->
        <div class="video-container">
          <div class="video-wrapper" :class="{ playing: isPlaying }">
            <video
              ref="video"
              :src="videoUrl"
              @loadedmetadata="onVideoLoaded"
              @loadeddata="onVideoLoaded"
              @canplay="onVideoLoaded"
              @timeupdate="onTimeUpdate"
              @play="isPlaying = true"
              @pause="isPlaying = false"
              @ended="onVideoEnded"
              class="video-player"
              preload="metadata"
            ></video>

            <!-- Video Overlay Controls -->
            <div class="video-overlay" @click="togglePlay">
              <v-btn
                v-if="!isPlaying"
                icon
                size="x-large"
                color="white"
                class="play-overlay-btn"
                variant="elevated"
              >
                <v-icon size="48">mdi-play</v-icon>
              </v-btn>
            </div>

            <!-- Loading State -->
            <div v-if="!videoLoaded" class="video-loading">
              <v-progress-circular
                indeterminate
                color="primary"
                size="64"
              ></v-progress-circular>
              <p class="mt-4">Loading video...</p>
            </div>

            <!-- Temporarily disabled -->
            <!-- Download Progress Overlay -->
            <!-- <div v-if="downloading" class="download-progress-overlay">
              <v-card class="progress-card" elevation="8">
                <v-card-title class="text-center">
                  <v-icon left color="primary">mdi-download</v-icon>
                  Processing Video
                </v-card-title>

                <v-card-text>
                  <div class="text-center mb-4">
                    <v-progress-circular
                      :value="downloadProgress"
                      color="primary"
                      size="80"
                      width="8"
                    >
                      {{ Math.round(downloadProgress) }}%
                    </v-progress-circular>
                  </div>

                  <v-progress-linear
                    :value="downloadProgress"
                    color="primary"
                    height="8"
                    rounded
                  ></v-progress-linear>

                  <p class="text-center mt-3 mb-0">{{ processingStep }}</p>

                  <div class="text-center mt-3">
                    <small class="text--secondary">
                      Trimming {{ formatDuration(trimOut - trimIn) }} of video
                    </small>
                  </div>
                </v-card-text>
              </v-card>
            </div> -->
          </div>
        </div>

        <!-- Enhanced Timeline Section -->
        <div class="timeline-section">
          <div class="timeline-header">
            <div class="timeline-info">
              <v-chip size="small" color="success" variant="outlined">
                <v-icon left size="16">mdi-scissors-cutting</v-icon>
                {{ formatTime(trimIn) }} — {{ formatTime(trimOut) }}
              </v-chip>
              <span class="ml-4 text-body-2 text-medium-emphasis">
                Drag handles to adjust trim points
              </span>
            </div>

            <div class="timeline-actions">
              <v-btn
                @click="undo"
                :disabled="!canUndo"
                size="small"
                class="mr-1"
              >
                <v-icon>mdi-undo</v-icon>
              </v-btn>
              <v-btn @click="redo" :disabled="!canRedo" size="small">
                <v-icon>mdi-redo</v-icon>
              </v-btn>
            </div>
          </div>

          <!-- Interactive Timeline -->
          <div class="timeline-outer-container">
            <div class="timeline-container" ref="timelineContainer">
              <svg
                ref="timeline"
                class="timeline-svg"
                :width="Math.max(timelineWidth, minTimelineWidth)"
                height="80"
                :viewBox="`0 0 ${Math.max(timelineWidth, minTimelineWidth)} 80`"
                preserveAspectRatio="xMinYMin meet"
                @mousedown.passive="startScrub"
                @mousemove.passive="scrub"
                @mouseup="endScrub"
                @mouseleave="endScrub"
              >
                <!-- Background Track -->
                <rect
                  x="0"
                  y="30"
                  :width="timelineWidth"
                  height="20"
                  fill="#000"
                  rx="10"
                  class="timeline-bg"
                />

                <!-- Unselected regions (dimmed) -->
                <rect
                  v-if="trimIn > 0"
                  x="0"
                  y="30"
                  :width="timeToPx(trimIn)"
                  height="20"
                  fill="#999"
                  rx="10"
                />
                <rect
                  v-if="trimOut < duration"
                  :x="timeToPx(trimOut)"
                  y="30"
                  :width="timeToPx(duration - trimOut)"
                  height="20"
                  fill="#999"
                  rx="10"
                />

                <!-- Selected Trim Region -->
                <rect
                  :x="timeToPx(trimIn)"
                  y="30"
                  :width="Math.max(2, timeToPx(trimOut - trimIn))"
                  height="20"
                  fill="#468CF1"
                  rx="10"
                  class="trim-region"
                />

                <!-- Gradient Definition -->
                <defs>
                  <linearGradient
                    id="trimGradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop
                      offset="0%"
                      style="stop-color: #468cf1; stop-opacity: 1"
                    />
                    <stop
                      offset="100%"
                      style="stop-color: #2196f3; stop-opacity: 1"
                    />
                  </linearGradient>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                    <feMerge>
                      <feMergeNode in="coloredBlur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>

                <!-- Trim Handles -->
                <g
                  class="handle-group"
                  @mousedown.stop="startDrag('in', $event)"
                >
                  <circle
                    :cx="timeToPx(trimIn)"
                    cy="40"
                    r="12"
                    fill="white"
                    stroke="#468CF1"
                    stroke-width="3"
                    class="trim-handle in-handle"
                    filter="url(#glow)"
                  />
                  <rect
                    :x="timeToPx(trimIn) - 15"
                    y="25"
                    width="30"
                    height="30"
                    fill="transparent"
                    cursor="ew-resize"
                  />
                </g>

                <g
                  class="handle-group"
                  @mousedown.stop="startDrag('out', $event)"
                >
                  <circle
                    :cx="timeToPx(trimOut)"
                    cy="40"
                    r="12"
                    fill="white"
                    stroke="#468CF1"
                    stroke-width="3"
                    class="trim-handle out-handle"
                    filter="url(#glow)"
                  />
                  <rect
                    :x="timeToPx(trimOut) - 15"
                    y="25"
                    width="30"
                    height="30"
                    fill="transparent"
                    cursor="ew-resize"
                  />
                </g>

                <!-- Playhead -->
                <g v-if="playbackVisible" class="playhead">
                  <line
                    :x1="timeToPx(currentTime)"
                    :x2="timeToPx(currentTime)"
                    y1="15"
                    y2="65"
                    stroke="#FF5722"
                    stroke-width="3"
                    class="playhead-line"
                  />
                  <circle
                    :cx="timeToPx(currentTime)"
                    cy="15"
                    r="6"
                    fill="#FF5722"
                    class="playhead-dot"
                  />
                </g>

                <!-- Time Markers -->
                <g
                  v-for="marker in timeMarkers"
                  :key="marker.time"
                  class="time-marker"
                >
                  <line
                    :x1="timeToPx(marker.time)"
                    y1="50"
                    :x2="timeToPx(marker.time)"
                    y2="60"
                    stroke="#000"
                    stroke-width="1"
                  />
                  <text
                    :x="timeToPx(marker.time)"
                    y="75"
                    text-anchor="middle"
                    font-size="10"
                    fill="#000"
                    class="marker-text"
                  >
                    {{ marker.label }}
                  </text>
                </g>
              </svg>
            </div>
          </div>
        </div>

        <!-- Enhanced Control Panel -->
        <div class="controls-panel">
          <div class="playback-controls">
            <v-btn @click="seekToPrevious" :disabled="!canSeekPrevious">
              <v-icon>mdi-skip-previous</v-icon>
            </v-btn>
            <v-btn depressed @click="rewind" size="large">
              <v-icon>mdi-rewind-10</v-icon>
            </v-btn>
            <v-btn
              @click="togglePlay"
              color="primary"
              size="x-large"
              class="play-btn"
            >
              <v-icon size="32">{{
                isPlaying ? "mdi-pause" : "mdi-play"
              }}</v-icon>
            </v-btn>
            <v-btn @click="forward" size="large">
              <v-icon>mdi-fast-forward-10</v-icon>
            </v-btn>
            <v-btn @click="seekToNext" :disabled="!canSeekNext">
              <v-icon>mdi-skip-next</v-icon>
            </v-btn>
          </div>

          <div class="time-display">
            <div class="current-time">
              <span class="text-h6 font-mono">{{
                formatTime(currentTime)
              }}</span>
              <span class="text-body-2 text-medium-emphasis ml-1"
                >/ {{ formatTime(duration) }}</span
              >
            </div>

            <!-- Playback Speed -->
            <v-select
              v-model="playbackRate"
              :items="playbackRates"
              item-text="title"
              item-value="value"
              dense
              outlined
              class="speed-selector"
              hide-details
              @change="setPlaybackRate"
            >
              <template #selection="{ item }">
                <span class="text-caption">{{ item.title }}</span>
              </template>
            </v-select>
          </div>

          <div class="action-controls">
            <!-- Temporarily disabled -->
            <!-- <v-tooltip text="Download trimmed video">
              <template #activator="{ props }">
                <v-btn
                  icon
                  variant="text"
                  class="action-control-btn"
                  @click="downloadTrimmedVideo"
                  v-bind="props"
                  :loading="downloading"
                  :disabled="downloading || !videoLoaded"
                >
                  <v-icon>{{
                    downloading ? "mdi-loading mdi-spin" : "mdi-download"
                  }}</v-icon>
                </v-btn>
              </template>
            </v-tooltip> -->

            <!-- Keyboard Shortcuts Help -->
            <v-tooltip text="Jump to trim start">
              <template #activator="{ props }">
                <v-btn
                  icon
                  variant="text"
                  class="action-control-btn"
                  v-bind="props"
                  @click="showShortcuts = !showShortcuts"
                >
                  <v-icon>mdi-keyboard</v-icon>
                </v-btn>
              </template>
              <span>Keyboard shortcuts</span>
            </v-tooltip>
          </div>
        </div>

        <!-- Shortcuts Dialog -->
        <v-dialog v-model="showShortcuts" persistent width="500">
          <v-card>
            <v-card-title>Keyboard Shortcuts</v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <div class="shortcuts-list mt-4">
                <div
                  class="shortcut-item"
                  v-for="shortcut in shortcuts"
                  :key="shortcut.key"
                >
                  <v-chip size="small" variant="outlined">{{
                    shortcut.key
                  }}</v-chip>
                  <span class="ml-2">{{ shortcut.desc }}</span>
                </div>
              </div>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn @click="showShortcuts = false">Close</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card>
    </v-dialog>

    <!-- Processing Overlay Dialog -->
    <v-dialog
      v-model="isProcessing"
      persistent
      fullscreen
      class="processing-dialog"
    >
      <div class="processing-overlay">
        <v-card class="processing-card" elevation="8">
          <v-card-title class="text-center d-flex align-center justify-center">
            <v-icon left color="primary" size="32" class="mr-2">
              mdi-video-box
            </v-icon>
            Processing Video Trim
          </v-card-title>

          <v-card-text class="text-center pa-6">
            <div class="progress-circle mb-6">
              <v-progress-circular
                :value="processingProgress"
                color="primary"
                size="120"
                width="8"
              >
                {{ Math.round(processingProgress) }}%
              </v-progress-circular>
            </div>

            <div class="status-text text-h6 mb-4">
              {{ processingStatus }}
            </div>

            <div class="text-body-2 text-medium-emphasis">
              Please don't close this window while processing
            </div>
          </v-card-text>
        </v-card>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import ResponseHelper from "../../utils/ResponseHelper";

export default {
  name: "TrimEditor",
  props: {
    value: Boolean,
    recordingId: String,
    videoUrl: String,
    duration: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      dialog: false,
      videoLoaded: false,

      toast: new ResponseHelper(),

      // Trim state
      trimIn: 0,
      trimOut: 0,

      // Interaction state
      dragging: null,
      startX: 0,
      startValue: 0,
      scrubbing: false,

      // Playback state
      currentTime: 0,
      isPlaying: false,
      playbackRate: 1,

      // UI state
      timelineWidth: 1200,
      showShortcuts: false,

      // Download state (temporarily disabled)
      // downloading: false,
      // downloadProgress: 0,
      // processingStep: "",

      // History management
      history: [],
      historyIndex: -1,
      maxHistorySize: 50,

      // Save state
      saving: false,

      // Job tracking
      jobId: null,
      isProcessing: false,
      processingStatus: "",
      processingProgress: 0,
      jobCheckInterval: null,
      consecutiveErrors: 0,

      // Configuration
      minTrimDuration: 0.1,
      seekStep: 10,

      // Minimum timeline width
      minTimelineWidth: 1200,

      playbackRates: [
        { title: "0.5x", value: 0.5 },
        { title: "1x", value: 1 },
        { title: "1.5x", value: 1.5 },
        { title: "2x", value: 2 },
        { title: "2.5x", value: 2.5 },
        { title: "3x", value: 3 },
      ],

      shortcuts: [
        { key: "Space", desc: "Play/Pause" },
        { key: "←/→", desc: "Seek 5s" },
        { key: "Shift+←/→", desc: "Seek 10s" },
        { key: "Ctrl+Z", desc: "Undo" },
        { key: "Ctrl+Y", desc: "Redo" },
        { key: "R", desc: "Reset trim" },
        { key: "Enter", desc: "Apply trim" },
        { key: "Esc", desc: "Close editor" },
      ],
    };
  },

  computed: {
    timeMarkers() {
      const markers = [];
      const step = this.duration > 300 ? 60 : this.duration > 60 ? 30 : 10;

      for (let i = 0; i <= this.duration; i += step) {
        markers.push({
          time: i,
          label: this.duration > 60 ? this.formatTime(i) : `${i}s`,
        });
      }

      // Always add duration marker if not already present
      if (this.duration % step !== 0) {
        markers.push({
          time: this.duration,
          label:
            this.duration > 60
              ? this.formatTime(this.duration)
              : `${Math.floor(this.duration)}s`,
        });
      }

      return markers;
    },

    canUndo() {
      return this.historyIndex > 0;
    },

    canRedo() {
      return this.historyIndex < this.history.length - 1;
    },

    playbackVisible() {
      return this.isPlaying || this.scrubbing;
    },

    canSeekPrevious() {
      return this.currentTime > 5;
    },

    canSeekNext() {
      return this.currentTime < this.duration - 5;
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.dialog = val;
        if (val) {
          // Reset states when opening modal
          this.saving = false;
          this.isProcessing = false;
          this.jobId = null;
          this.processingStatus = "";
          this.processingProgress = 0;

          this.init();
          this.$nextTick(() => {
            this.updateTimelineWidth();
            // Check if video is already loaded (cached)
            const video = this.$refs.video;
            if (video && video.readyState >= 1) {
              this.onVideoLoaded();
            }
          });
        }
      },
    },

    dialog(val) {
      if (!val) {
        this.$emit("input", false);
        this.cleanup();
      }
    },

    duration: {
      immediate: true,
      handler(val) {
        if (val > 0 && this.trimOut === 0) {
          this.trimOut = val;
        }
      },
    },
  },

  methods: {
    // used only the second argument of mapActions
    ...mapActions("recordings", [
      "my_recording_details",
      "save_trimmed_video",
      "check_job_status",
    ]),

    init() {
      this.trimIn = 0;
      this.trimOut = this.duration || 0;
      this.currentTime = 0;
      this.isPlaying = false;
      this.scrubbing = false;
      this.dragging = null;
      this.videoLoaded = false;
      this.playbackRate = 1;

      // Initialize history
      this.history = [{ in: this.trimIn, out: this.trimOut }];
      this.historyIndex = 0;

      this.setupKeyboardListeners();
    },

    cleanup() {
      this.removeKeyboardListeners();
      this.revert();
      const video = this.$refs.video;
      if (video) {
        video.pause();
      }
    },

    onVideoLoaded() {
      const video = this.$refs.video;
      if (video && video.readyState >= 1 && !this.videoLoaded) {
        this.videoLoaded = true;
        if (this.duration === 0) {
          this.trimOut = video.duration;
          this.duration = video.duration;
        }
      }
    },

    updateTimelineWidth() {
      if (this.$refs.timelineContainer) {
        const containerWidth = this.$refs.timelineContainer.clientWidth;

        // Calculate width based on duration
        const widthPerSecond = 10; // Pixels per second
        const calculatedWidth = Math.max(
          containerWidth,
          this.duration * widthPerSecond
        );

        this.timelineWidth = calculatedWidth;
      }
    },

    // Time conversion utilities
    timeToPx(time) {
      return Math.max(0, (time / (this.duration || 1)) * this.timelineWidth);
    },

    pxToTime(px) {
      return Math.max(
        0,
        Math.min(
          this.duration,
          (px / this.timelineWidth) * (this.duration || 1)
        )
      );
    },

    formatTime(seconds) {
      if (seconds < 0 || isNaN(seconds)) return "00:00";

      const hrs = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const secs = Math.floor(seconds % 60);

      if (hrs > 0) {
        return `${hrs}:${mins.toString().padStart(2, "0")}:${secs
          .toString()
          .padStart(2, "0")}`;
      }
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    },

    formatDuration(seconds) {
      if (seconds < 60) {
        return `${Math.round(seconds)}s`;
      } else if (seconds < 3600) {
        const mins = Math.floor(seconds / 60);
        const secs = Math.round(seconds % 60);
        return `${mins}m ${secs}s`;
      } else {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        return `${hrs}h ${mins}m`;
      }
    },

    // Drag handling
    startDrag(type, event) {
      this.dragging = type;
      const video = this.$refs.video;
      if (video) video.pause();

      // Get timeline dimensions and position
      const rect = this.$refs.timeline.getBoundingClientRect();
      this.timelineRect = rect;
      this.timelineLeft = rect.left;
      this.timelineWidth = rect.width;

      // Store initial values
      this.startX = event.clientX;
      this.startValue = type === "in" ? this.trimIn : this.trimOut;

      event.preventDefault();
      event.stopPropagation();
    },

    drag(event) {
      if (!this.dragging || !this.duration) return;

      // Calculate position relative to timeline
      const cursorX = event.clientX - this.timelineLeft;
      const position = (cursorX / this.timelineWidth) * this.duration;

      if (this.dragging === "in") {
        // Handle IN point drag
        const newIn = Math.max(
          0,
          Math.min(position, this.trimOut - this.minTrimDuration)
        );
        this.trimIn = newIn;
        this.seekTo(newIn);
      } else if (this.dragging === "out") {
        // Handle OUT point drag
        const newOut = Math.min(
          this.duration,
          Math.max(position, this.trimIn + this.minTrimDuration)
        );
        this.trimOut = newOut;
        this.seekTo(newOut);
      }
    },

    stopDrag() {
      if (this.dragging) {
        this.saveToHistory();
        this.dragging = null;
      }
    },

    // Timeline scrubbing
    startScrub(event) {
      if (this.dragging) return;
      this.scrubbing = true;
      this.seekToPosition(event);
    },

    scrub(event) {
      if (this.scrubbing && !this.dragging) {
        this.seekToPosition(event);
      } else if (this.dragging) {
        this.drag(event);
      }
    },

    endScrub() {
      this.scrubbing = false;
      this.stopDrag();
    },

    scrollToPlayhead() {
      const container = this.$refs.timelineContainer;
      if (!container) return;

      const playheadX = this.timeToPx(this.currentTime);
      const containerWidth = container.clientWidth;
      const scrollLeft = container.scrollLeft;
      const margin = 100; // Pixels margin from edge

      // Scroll if playhead is near the edges
      if (playheadX < scrollLeft + margin) {
        container.scrollLeft = Math.max(0, playheadX - margin);
      } else if (playheadX > scrollLeft + containerWidth - margin) {
        container.scrollLeft = playheadX - containerWidth + margin;
      }
    },

    seekToPosition(event) {
      if (!this.duration) return;

      const container = this.$refs.timelineContainer;
      const rect = this.$refs.timeline.getBoundingClientRect();
      const scrollOffset = container.scrollLeft;

      const x = Math.max(
        0,
        Math.min(this.timelineWidth, event.clientX - rect.left + scrollOffset)
      );

      const time = (x / this.timelineWidth) * this.duration;
      this.seekTo(time);
    },

    seekTo(time) {
      const video = this.$refs.video;
      if (video) {
        const clampedTime = Math.max(0, Math.min(this.duration, time));
        video.currentTime = clampedTime;
        this.currentTime = clampedTime;
      }
    },

    // Playback controls
    onTimeUpdate() {
      const video = this.$refs.video;
      if (!video) return;

      this.currentTime = video.currentTime;
      this.scrollToPlayhead();

      // Auto-loop within trim range
      if (video.currentTime >= this.trimOut && this.isPlaying) {
        video.currentTime = this.trimIn;
      }
    },

    onVideoEnded() {
      this.seekTo(this.trimIn);
    },

    togglePlay() {
      const video = this.$refs.video;
      if (!video) return;

      if (video.paused) {
        // If at or past trim out, start from trim in
        if (video.currentTime >= this.trimOut) {
          video.currentTime = this.trimIn;
        }
        video.play();
      } else {
        video.pause();
      }
    },

    rewind() {
      this.seekTo(Math.max(this.trimIn, this.currentTime - this.seekStep));
    },

    forward() {
      this.seekTo(Math.min(this.trimOut, this.currentTime + this.seekStep));
    },

    seekToPrevious() {
      this.seekTo(Math.max(0, this.currentTime - 5));
    },

    seekToNext() {
      this.seekTo(Math.min(this.duration, this.currentTime + 5));
    },

    setPlaybackRate(rate) {
      const video = this.$refs.video;
      if (video) {
        video.playbackRate = rate;
        this.playbackRate = rate;
      }
    },

    // History management
    saveToHistory() {
      // Don't save if values haven't changed
      const lastState = this.history[this.historyIndex];
      if (
        lastState &&
        lastState.in === this.trimIn &&
        lastState.out === this.trimOut
      ) {
        return;
      }

      // Remove future states
      this.history = this.history.slice(0, this.historyIndex + 1);

      // Add new state
      this.history.push({
        in: this.trimIn,
        out: this.trimOut,
      });

      // Limit history size
      if (this.history.length > this.maxHistorySize) {
        this.history.shift();
      } else {
        this.historyIndex++;
      }
    },

    undo() {
      if (this.canUndo) {
        this.historyIndex--;
        const state = this.history[this.historyIndex];
        this.trimIn = state.in;
        this.trimOut = state.out;
        this.seekTo(this.trimIn);
      }
    },

    redo() {
      if (this.canRedo) {
        this.historyIndex++;
        const state = this.history[this.historyIndex];
        this.trimIn = state.in;
        this.trimOut = state.out;
        this.seekTo(this.trimIn);
      }
    },

    // Actions
    revert() {
      this.trimIn = 0;
      this.trimOut = this.duration;
      this.seekTo(0);
      const video = this.$refs.video;
      if (video) video.pause();
      this.saveToHistory();
    },

    close() {
      if (this.isProcessing && !this.processingProgress === 100) {
        const confirmClose = confirm(
          "Video processing is still in progress. Closing will not cancel the process, but you won't see the progress. Continue?"
        );

        if (!confirmClose) return;
      }

      // Always cleanup and close
      this.cleanupJobTracking();
      this.dialog = false;
      this.$emit("input", false);
    },

    async saveTrimmedVideo() {
      if (this.saving || this.isProcessing) return;

      try {
        // Set initial states
        this.saving = true;
        this.isProcessing = true;
        this.processingProgress = 0;
        this.processingStatus = "Initializing trim process...";

        const response = await this.save_trimmed_video({
          id: this.recordingId,
          start_time: this.trimIn,
          end_time: this.trimOut,
        });

        // Get job ID from the response data
        const jobId = response.data?.job_id;

        if (!jobId) {
          throw new Error("No job ID received from server");
        }

        // Initialize tracking
        this.jobId = jobId;
        this.saving = false;

        // Start tracking immediately
        this.startJobTracking();

        // Show success toast
        this.toast.sendSuccess({
          message: "Trim process started successfully.",
        });
      } catch (err) {
        console.error("Failed to start trim job:", err);
        this.toast.sendError({
          message:
            err.response?.data?.message || "Failed to start trim process",
        });

        // Reset states on error
        this.stopJobTracking();
        this.saving = false;
        this.isProcessing = false;
        this.processingProgress = 0;
        this.processingStatus = "";
        this.jobId = null;
      }
    },

    // Temporarily disabled until backend support
    // async downloadTrimmedVideo() {
    //   this.downloading = true;
    //   this.downloadProgress = 0;
    //   this.processingStep = "Preparing download...";

    //   try {
    //     // code block goes here

    //     // Show success message
    //     this.toast.sendSuccess({ message: "Video downloaded successfully!" });
    //   } catch (err) {
    //     console.error("Download failed:", err);
    //     this.processingStep = "Download failed";

    //     this.toast.sendError({ message: err });
    //   } finally {
    //     // Reset after a short delay
    //     setTimeout(() => {
    //       this.downloading = false;
    //       this.downloadProgress = 0;
    //       this.processingStep = "";
    //     }, 2000);
    //   }
    // },

    // Keyboard shortcuts
    setupKeyboardListeners() {
      document.addEventListener("keydown", this.handleKeyDown);
    },

    removeKeyboardListeners() {
      document.removeEventListener("keydown", this.handleKeyDown);
    },

    handleKeyDown(event) {
      if (!this.dialog || this.isProcessing) return;

      const { key, ctrlKey, shiftKey } = event;

      switch (key) {
        case " ":
          event.preventDefault();
          this.togglePlay();
          break;
        case "ArrowLeft":
          event.preventDefault();
          if (shiftKey) {
            this.rewind();
          } else {
            this.seekTo(this.currentTime - 5);
          }
          break;
        case "ArrowRight":
          event.preventDefault();
          if (shiftKey) {
            this.forward();
          } else {
            this.seekTo(this.currentTime + 5);
          }
          break;
        case "r":
        case "R":
          event.preventDefault();
          this.revert();
          break;
        case "Enter":
          event.preventDefault();
          this.saveTrimmedVideo();
          break;
        case "Escape":
          event.preventDefault();
          this.close();
          break;
        case "z":
        case "Z":
          if (ctrlKey) {
            event.preventDefault();
            this.undo();
          }
          break;
        case "y":
        case "Y":
          if (ctrlKey) {
            event.preventDefault();
            this.redo();
          }
          break;
      }
    },

    startJobTracking() {
      if (this.jobCheckInterval) {
        clearInterval(this.jobCheckInterval);
      }

      // Initial delay before first check
      setTimeout(() => {
        this.pollJobStatus();
      }, 1000);
    },

    async pollJobStatus() {
      if (!this.jobId || !this.isProcessing) {
        this.stopJobTracking();
        return;
      }

      try {
        await this.checkJobStatus();

        // Schedule next poll with exponential backoff
        const baseDelay = 2000; // Start with 2 second delay
        const maxDelay = 5000; // Max 5 seconds between polls
        const currentDelay = Math.min(
          baseDelay * (1 + this.consecutiveErrors),
          maxDelay
        );

        this.jobCheckInterval = setTimeout(() => {
          this.pollJobStatus();
        }, currentDelay);

        // Reset error counter on success
        this.consecutiveErrors = 0;
      } catch (err) {
        if (err.response?.status === 429) {
          this.consecutiveErrors++;
          console.log(
            `Rate limited, backing off (attempt ${this.consecutiveErrors})`
          );

          // Wait longer before next attempt
          setTimeout(() => {
            this.pollJobStatus();
          }, 5000 * this.consecutiveErrors);
        } else {
          this.handleJobError(err);
        }
      }
    },

    stopJobTracking() {
      if (this.jobCheckInterval) {
        clearTimeout(this.jobCheckInterval);
        this.jobCheckInterval = null;
      }
      this.consecutiveErrors = 0;
    },

    async checkJobStatus() {
      if (!this.jobId) return;

      try {
        const response = await this.check_job_status(this.jobId);
        const data = response.data || response;

        if (data.progress) {
          const ffmpegProgress = parseInt(data.progress) || 0;
          this.processingProgress = 20 + ffmpegProgress * 0.6;
        } else {
          this.processingProgress = 0;
        }

        this.processingStatus = data.status || "Processing...";

        // Handle completion
        if (data.completed) {
          if (data.success) {
            this.processingProgress = 100;
            this.processingStatus = "Video trim completed successfully!";

            // Wait for animation
            await new Promise((resolve) => setTimeout(resolve, 1500));

            // Show success message before cleanup
            this.toast.sendSuccess({
              message: "Video trimming completed successfully!",
            });

            // Cleanup and close after toast
            setTimeout(() => {
              this.cleanupJobTracking();
              this.dialog = false;
              this.$emit("input", false);
            }, 500);

            // Add a small delay before reload to ensure toast is visible
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          } else {
            throw new Error(data.error || "Processing failed");
          }
        }
      } catch (err) {
        console.error("Job status check failed:", err);

        if (err.response?.status === 429) {
          // Let the startJobTracking handle the backoff
          throw err;
        } else if (err.response?.status === 404) {
          this.toast.sendError({ message: "Job not found or expired" });
          this.cleanupJobTracking();
        } else if (err.response?.status === 500) {
          this.toast.sendError({ message: "Processing error occurred" });
          this.cleanupJobTracking();
        }
      }
    },
    handleJobError(err) {
      console.error("Job status check failed:", err);
      if (err.response?.status === 404) {
        this.toast.sendError({ message: "Job not found or expired" });
        this.cleanupJobTracking();
      } else if (err.response?.status === 500) {
        this.toast.sendError({ message: "Processing error occurred" });
        this.cleanupJobTracking();
      }
    },
    cleanupJobTracking() {
      this.stopJobTracking();
      this.jobId = null;
      this.isProcessing = false;
      this.processingStatus = "";
      this.processingProgress = 0;
      this.saving = false;
    },
  },

  mounted() {
    // Bind global event listeners
    this._boundDrag = this.drag.bind(this);
    this._boundStopDrag = this.stopDrag.bind(this);
    this._boundResize = this.updateTimelineWidth.bind(this);

    window.addEventListener("mousemove", this._boundDrag);
    window.addEventListener("mouseup", this._boundStopDrag);
    window.addEventListener("resize", this._boundResize);

    // Initial timeline width setup
    this.$nextTick(() => {
      this.updateTimelineWidth();
    });
  },

  beforeDestroy() {
    // Clean up event listeners
    if (this._boundDrag)
      window.removeEventListener("mousemove", this._boundDrag);
    if (this._boundStopDrag)
      window.removeEventListener("mouseup", this._boundStopDrag);
    if (this._boundResize)
      window.removeEventListener("resize", this._boundResize);

    // Ensure proper cleanup of intervals and states
    this.removeKeyboardListeners();
    this.cleanupJobTracking();

    // Clear any pending timeouts
    if (this.jobCheckInterval) {
      clearTimeout(this.jobCheckInterval);
    }
  },
};
</script>

<style scoped>
.trim-editor {
  background: linear-gradient(135deg, #e7e7e7 0%, #2d2d2d 100%);
  color: white;
  height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Enhanced Toolbar */
.toolbar-enhanced {
  height: 63px !important;
  backdrop-filter: blur(10px);
  background: #fff !important;
  color: #000 !important;
}

.toolbar-title {
  display: flex;
  align-items: center;
}

.close-btn {
  transition: transform 0.2s ease;
}

.close-btn:hover {
  transform: rotate(90deg);
}

.duration-chip {
  font-family: "Roboto Mono", monospace;
  font-weight: 500;
}

.save-btn {
  min-width: 120px;
  font-weight: 600;
}

.processing-banner {
  border-radius: 0 !important;
  margin: 0 !important;
}

.processing-banner .v-alert__content {
  width: 100%;
}

/* Video Container */
.video-container {
  /* flex: 1; */
  /* display: flex; */
  align-items: center;
  justify-content: center;
  background: #000;
  position: relative;
  overflow: hidden;
}

.video-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: filter 0.3s ease;
}

.video-wrapper.playing .video-overlay {
  opacity: 0;
  pointer-events: none;
}

.video-player {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: opacity 0.3s ease;
}

.play-overlay-btn {
  transform: scale(1);
  transition: transform 0.2s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
}

.play-overlay-btn:hover {
  transform: scale(1.1);
}

.video-loading {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.7);
}

/* Timeline Section */
.timeline-section {
  background: #dedede !important;
  color: #000 !important;
  backdrop-filter: blur(10px);
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.timeline-info {
  display: flex;
  align-items: center;
}

.timeline-container {
  background: #fafafa !important;
  color: #000 !important;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
  scrollbar-width: thin;
  position: relative;
}

/* Customize the scrollbar */
.timeline-container::-webkit-scrollbar {
  height: 8px;
}

.timeline-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.timeline-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.timeline-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.timeline-svg {
  cursor: crosshair;
  border-radius: 8px;
  display: block; /* Important for proper sizing */
  min-width: 100%;
}

.timeline-bg {
  transition: fill 0.2s ease;
}

.trim-region {
  transition: all 0.2s ease;
  cursor: pointer;
}

.trim-region:hover {
  filter: brightness(1.1);
}

.trim-handle {
  transition: all 0.2s ease;
  cursor: ew-resize;
}

.handle-group:hover .trim-handle {
  r: 15;
  stroke-width: 4;
}

.in-handle {
  filter: drop-shadow(0 0 8px rgba(76, 175, 80, 0.6));
}

.out-handle {
  filter: drop-shadow(0 0 8px rgba(33, 150, 243, 0.6));
}

.time-marker {
  transition: opacity 0.2s ease;
}

.marker-text {
  font-family: "Roboto Mono", monospace;
  font-size: 9px;
}

/* Controls Panel */
.controls-panel {
  background: #fff;
  color: #000;
  backdrop-filter: blur(10px);
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  min-height: 80px;
}

.playback-controls {
  display: flex;
  align-items: center;
}

.control-group {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  overflow: hidden;
}

.play-btn {
  background: linear-gradient(45deg, #468cf1, #468cf1) !important;
  margin: 0 8px;
  box-shadow: 0 4px 16px rgba(76, 175, 80, 0.3);
}

.time-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.current-time {
  display: flex;
  align-items: baseline;
  font-family: "Roboto Mono", monospace;
}

.speed-selector {
  width: 80px;
}

.action-controls {
  display: flex;
  gap: 8px;
}

.action-control-btn {
  background: rgba(33, 150, 243, 0.9) !important;
  color: white !important;
  backdrop-filter: blur(10px);
}

/* Shortcuts Dialog */
.shortcuts-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  /* max-height: 300px; */
  overflow-y: auto;
}

.shortcut-item {
  display: flex;
  align-items: center;
  padding: 4px 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .controls-panel {
    flex-direction: column;
    gap: 16px;
    padding: 16px;
  }

  .timeline-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .toolbar-title .text-h6 {
    font-size: 1.1rem;
  }

  .duration-chip {
    font-size: 0.8rem;
  }
}

/* Animations */
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.playhead-line,
.playhead-dot {
  animation: pulse 2s ease-in-out infinite;
}

/* Focus and Accessibility */
.v-btn:focus-visible {
  outline: 2px solid #468cf1;
  outline-offset: 2px;
}

/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

/* Transitions */
* {
  transition: color 0.2s ease, background-color 0.2s ease, opacity 0.2s ease;
}

/* Loading States */
.v-btn.v-btn--loading {
  pointer-events: none;
}

/* Enhanced Hover Effects */
/* .v-btn:not(.v-btn--disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
} */

.timeline-svg:hover .timeline-bg {
  fill: rgba(255, 255, 255, 0.15);
}

/* Dark theme enhancements 1e1e1e */
.v-dialog > .v-card {
  background: #b8b8b8 !important;
}

.v-toolbar {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
}

/* Download Progress Overlay */
.download-progress-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.progress-card {
  min-width: 320px;
  max-width: 400px;
  padding: 20px;
  background: #1e1e1e !important;
  color: white !important;
}

.progress-card .v-card-title {
  color: white !important;
  font-size: 1.2rem;
  padding-bottom: 8px;
}

.progress-card .v-card-text {
  color: white !important;
}

.progress-card .v-progress-circular {
  margin: 0 auto;
}

.progress-card .v-progress-linear {
  margin-top: 16px;
}

.progress-card .text--secondary {
  color: rgba(255, 255, 255, 0.7) !important;
}

/* Performance optimizations */
.video-player,
.timeline-svg,
.trim-region,
.trim-handle {
  will-change: transform;
}

/* .processing-dialog {
  background: rgba(0, 0, 0, 0.9) !important;
  backdrop-filter: blur(10px);
  z-index: 9999 !important;
}

.processing-card {
  min-width: 400px;
  max-width: 500px;
  padding: 32px;
  border-radius: 16px;
  background: white !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2) !important;
} */

/* Add smooth transitions for progress updates */
/* .v-progress-circular,
.v-progress-linear {
  transition: all 0.3s ease-out !important;
}

.status-text {
  transition: color 0.3s ease, opacity 0.3s ease;
} */

/* Add progress animation */
/* @keyframes progress-pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
  }
}

.processing-card .v-progress-circular {
  animation: progress-pulse 2s infinite ease-in-out;
} */

.processing-overlay {
  height: 100vh;
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5) !important;
  backdrop-filter: blur(10px);
  z-index: 9999 !important;
}

.processing-card {
  min-width: 400px;
  max-width: 500px;
  padding: 32px;
  border-radius: 16px;
  background: white !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2) !important;
}

.progress-circle {
  display: flex;
  justify-content: center;
  margin: 20px 0;
}

.status-text {
  color: #1867c0;
  font-weight: 500;
  line-height: 1.4;
  transition: color 0.3s ease, opacity 0.3s ease;
}

/* Smooth transitions */
.v-progress-circular,
.v-progress-linear__determinate {
  transition: all 0.3s ease-in-out;
}

/* Dialog transition */
.v-dialog-transition-enter-active,
.v-dialog-transition-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.v-dialog-transition-enter,
.v-dialog-transition-leave-to {
  opacity: 0;
}

/* Add progress animation */
@keyframes progress-pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
  }
}

.processing-card .v-progress-circular {
  animation: progress-pulse 2s infinite ease-in-out;
}
</style>
